import { z } from 'zod';

const ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const MONEY = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal string with up to 4 fractional digits');

export const journalSourceSchema = z.enum([
  'manual',
  'bank_match',
  'reconciliation_adjustment',
  'recurring',
  'import',
  'payroll',
  'system',
  'check',
  'deposit',
  'invoice',
  'invoice_payment',
  'bill',
  'bill_payment',
  'year_end_close',
  'depreciation',
  'asset_disposal',
  'loan_payment',
  'loan_origination',
  'brokerage_statement',
  'shareholder_loan_payment',
  'shareholder_loan_origination',
  'intercompany',
  // Weekly liquor cost of goods sold pushed in by the inventory app (exports surface).
  'inventory_sync',
]);

export const journalLineInputSchema = z.object({
  accountId: z.string().uuid(),
  debit: MONEY.default('0'),
  credit: MONEY.default('0'),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  memo: z.string().max(500).nullable().optional(),
  bankTransactionId: z.string().uuid().nullable().optional(),
  // Set on a line CREDITING a receivable to clear an earlier accrual; points at
  // the A/R debit line being settled. This is what makes an open receivable
  // detectable — the accrual and its settlement are separate entries.
  settlesJournalLineId: z.string().uuid().nullable().optional(),
});
export type JournalLineInput = z.infer<typeof journalLineInputSchema>;

export const createJournalEntryInputSchema = z.object({
  date: ISO_DATE,
  memo: z.string().max(500).nullable().optional(),
  source: journalSourceSchema.default('manual'),
  reference: z.string().max(100).nullable().optional(),
  lines: z.array(journalLineInputSchema).min(2),
});
export type CreateJournalEntryInput = z.infer<typeof createJournalEntryInputSchema>;

/** Edit a manual journal entry in place (keeps the entry number). Source is not editable. */
export const updateJournalEntryInputSchema = z.object({
  date: ISO_DATE,
  memo: z.string().max(500).nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  lines: z.array(journalLineInputSchema).min(2),
  /**
   * Owner override: bypass the reconciliation lock and the period lock. When
   * set, per-line locks and the entry's isLocked flag are cleared as part of
   * the edit — the reconciliation record itself is preserved (so the hash
   * chain remains valid) but the entry is no longer considered reconciled
   * until the recon is re-run. Reason is required for the audit trail.
   */
  override: z.boolean().optional(),
  overrideReason: z.string().min(1).max(500).optional(),
});
export type UpdateJournalEntryInput = z.infer<typeof updateJournalEntryInputSchema>;

export const voidJournalEntryInputSchema = z.object({
  voidReason: z.string().min(1).max(500),
  /** Owner override: allow voiding an entry that is locked by a reconciliation. */
  override: z.boolean().optional(),
});
export type VoidJournalEntryInput = z.infer<typeof voidJournalEntryInputSchema>;

/**
 * Why an entry was voided.
 *
 * A picked list rather than free text, for the same reason reclassification has
 * one: "how many entries did I void as duplicates this year?" is a question an
 * auditor asks, and prose cannot answer it. The reason is still stored as a
 * plain string on the entry — the label IS the stored value, optionally
 * followed by a note — so nothing downstream has to know about these codes and
 * historic void reasons remain readable.
 */
export const VOID_REASONS = [
  { code: 'duplicate', label: 'Delete duplicate entry' },
  { code: 'wrong_amount', label: 'Wrong amount' },
  { code: 'wrong_account', label: 'Posted to the wrong account' },
  { code: 'wrong_date', label: 'Wrong date or period' },
  { code: 'wrong_company', label: 'Posted to the wrong company' },
  { code: 'never_happened', label: 'Transaction never happened / was reversed' },
  { code: 'import_error', label: 'Import error' },
  { code: 'test_entry', label: 'Test or practice entry' },
  { code: 'accountant_adjustment', label: 'Accountant / CPA instruction' },
  { code: 'other', label: 'Other — explain below' },
] as const;

export type VoidReasonCode = (typeof VOID_REASONS)[number]['code'];

/**
 * Build the stored reason string from a picked label plus an optional note.
 * Kept here so every void surface composes it identically and the audit log
 * stays greppable by label.
 */
export function composeVoidReason(label: string, note?: string | null): string {
  const trimmed = (note ?? '').trim();
  return trimmed ? `${label} — ${trimmed}` : label;
}

/**
 * Why an entry was re-accounted. A fixed list rather than free text so the
 * audit log can be grouped and counted — "show me every payroll breakout this
 * year" is a question an auditor actually asks, and free text cannot answer it.
 */
export const RECLASSIFY_REASONS = [
  { code: 'misclassified', label: 'Posted to the wrong account' },
  { code: 'payroll_breakout', label: 'Broke payroll out into wages / taxes / benefits' },
  { code: 'split_detail', label: 'Split a lump sum into detail accounts' },
  { code: 'personal_business', label: 'Corrected the personal vs. business split' },
  { code: 'capitalize_expense', label: 'Changed between capitalizing and expensing' },
  { code: 'contact_correction', label: 'Corrected the vendor or customer' },
  { code: 'coa_cleanup', label: 'Chart of accounts cleanup or account merge' },
  { code: 'accountant_adjustment', label: 'Accountant / CPA adjustment' },
  { code: 'other', label: 'Other — explain below' },
] as const;

export type ReclassifyReasonCode = (typeof RECLASSIFY_REASONS)[number]['code'];

export const reclassifyReasonCodeSchema = z.enum(
  RECLASSIFY_REASONS.map((r) => r.code) as unknown as [ReclassifyReasonCode, ...ReclassifyReasonCode[]],
);

/**
 * Re-account an entry WITHOUT unwinding its reconciliation.
 *
 * Reconciling a checking account proves that a given amount moved on a given
 * date. It says nothing about which expense account the other side belongs in.
 * So the reconciled bank line — its account, its debit and its credit — is
 * frozen, along with the entry date; every other line may be re-accounted and
 * split freely. The reconciliation stays valid and the hash chain is untouched,
 * because the chain covers the reconciliation record, not the entry's lines.
 *
 * This is distinct from `update` with `override`, which BREAKS the lock.
 */
export const reclassifyJournalEntryInputSchema = z.object({
  lines: z.array(journalLineInputSchema).min(2),
  reasonCode: reclassifyReasonCodeSchema,
  reasonNote: z.string().max(500).nullable().optional(),
});
export type ReclassifyJournalEntryInput = z.infer<typeof reclassifyJournalEntryInputSchema>;

export const journalLineSchema = z.object({
  id: z.string().uuid(),
  journalEntryId: z.string().uuid(),
  accountId: z.string().uuid(),
  debit: MONEY,
  credit: MONEY,
  locationId: z.string().uuid().nullable(),
  classId: z.string().uuid().nullable(),
  contactId: z.string().uuid().nullable(),
  memo: z.string().nullable(),
  bankTransactionId: z.string().uuid().nullable(),
  lineOrder: z.number(),
  createdAt: z.string(),
  /**
   * Signed running balance of the line's account immediately after this line
   * posted (positive = on the account's normal side). Populated by
   * getJournalEntry; null when the enclosing entry is void.
   */
  balanceAfter: MONEY.nullable().optional(),
  /** The line's account normal balance side. Undefined on inputs / older responses. */
  normalBalance: z.enum(['debit', 'credit']).nullable().optional(),
  /**
   * The reconciliation that froze THIS line, if any.
   *
   * A reconciliation locks only the line posting to its own GL account, so the
   * other legs of the same entry stay available to reconcile against their own
   * statements — a credit-card payment is reconciled twice, once on the card
   * side and once on the bank side. The entry-level `isLocked` is only a
   * roll-up of these per-line locks, so it must not be read as "the whole
   * transaction is reconciled".
   */
  lockedByReconciliationId: z.string().uuid().nullable().optional(),
});
export type JournalLine = z.infer<typeof journalLineSchema>;

export const journalEntrySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  entryNumber: z.string(),
  date: ISO_DATE,
  memo: z.string().nullable(),
  source: journalSourceSchema,
  reference: z.string().nullable(),
  isVoid: z.boolean(),
  voidReason: z.string().nullable(),
  voidedAt: z.string().nullable(),
  isLocked: z.boolean(),
  lockedByReconciliationId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Sum of debits — populated on list responses. */
  total: z.string().optional(),
  /** "code name / code name" summary of accounts touched — list responses. */
  accountSummary: z.string().optional(),
  /**
   * Whether a bank-feed transaction points at this entry — list responses.
   * null = GL-only (nothing in the feed is linked to it).
   */
  bankMatchStatus: z.enum(['matched', 'reconciled']).nullable().optional(),
});
export type JournalEntry = z.infer<typeof journalEntrySchema>;

export const journalEntryWithLinesSchema = journalEntrySchema.extend({
  lines: z.array(journalLineSchema),
});
export type JournalEntryWithLines = z.infer<typeof journalEntryWithLinesSchema>;

export const listJournalEntriesQuerySchema = z.object({
  from: ISO_DATE.optional(),
  to: ISO_DATE.optional(),
  accountId: z.string().uuid().optional(),
  includeVoid: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .optional()
    .transform((v) => v === true || v === 'true'),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListJournalEntriesQuery = z.infer<typeof listJournalEntriesQuerySchema>;

// ── Duplicate entry detection ────────────────────────────────────────────────

/** One entry inside a group of exact duplicates. */
export interface DuplicateEntryItem {
  journalEntryId: string;
  entryNumber: string;
  source: string;
  memo: string | null;
  isLocked: boolean;
  createdAt: string;
  /**
   * Status of the bank transaction this entry is matched to, if any.
   * 'excluded' on a bank_match duplicate = the stale pending copy whose
   * posted twin was matched separately — the safe one to void.
   */
  bankTxStatus: string | null;
}

/**
 * Entries that are exactly alike: same date and the identical set of
 * account/debit/credit lines. Memo and source may differ — amounts hitting
 * the same accounts on the same date is what double-counts a report.
 */
export interface DuplicateEntryGroup {
  date: string;
  /** Total debits of one entry in the group (they are all equal). */
  total: string;
  /** Human summary of the accounts involved, e.g. "1000 Checking / 4006 Sales". */
  accountSummary: string;
  entries: DuplicateEntryItem[];
}

export interface DuplicateEntriesResponse {
  groups: DuplicateEntryGroup[];
  /** Total number of surplus entries (group sizes minus one each). */
  surplusCount: number;
}
